import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const FALLBACK_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{
    id: "osm", type: "raster", source: "osm",
    paint: { "raster-saturation": -0.88, "raster-brightness-max": 0.3, "raster-contrast": 0.32, "raster-opacity": 0.82 },
  }],
};

export function PlantGeoMap({ plants, selectedId, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const geometryKey = JSON.stringify(plants.map(({ id, name, coordinates }) => ({ id, name, coordinates })));

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: import.meta.env.VITE_MAP_STYLE_URL || FALLBACK_STYLE,
      center: [9.5, 48], zoom: 3.6, minZoom: 2.2, maxZoom: 16,
      attributionControl: true,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric", maxWidth: 110 }), "bottom-left");

    const locatedPlants = plants.filter((plant) => Array.isArray(plant.coordinates) && plant.coordinates.length === 2 && plant.coordinates.every(Number.isFinite) && Math.abs(plant.coordinates[0]) <= 180 && Math.abs(plant.coordinates[1]) <= 90);
    for (const plant of locatedPlants) {
      const element = document.createElement("button");
      element.type = "button";
      element.className = `geo-plant-marker ${plant.status}`;
      element.dataset.plantId = plant.id;
      element.title = plant.name;
      const label = document.createElement("span");
      const name = document.createElement("b");
      name.textContent = plant.name;
      label.append(name);
      element.append(document.createElement("i"), label);
      element.addEventListener("click", (event) => { event.stopPropagation(); selectRef.current(plant); });
      const marker = new maplibregl.Marker({ element, anchor: "center" }).setLngLat(plant.coordinates).addTo(map);
      markersRef.current.push({ marker, element, id: plant.id });
    }
    if (locatedPlants.length) {
      const bounds = locatedPlants.reduce((value, plant) => value.extend(plant.coordinates), new maplibregl.LngLatBounds());
      map.once("load", () => map.fitBounds(bounds, { padding: 75, maxZoom: 5.2, duration: 900 }));
    }
    return () => {
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = [];
      map.remove(); mapRef.current = null;
    };
  }, [geometryKey]);

  useEffect(() => {
    for (const { element, id } of markersRef.current) {
      const plant = plants.find(plant => plant.id === id);
      element.classList.remove('live', 'alarm', 'warning', 'offline', 'unmonitored');
      element.classList.add(plant?.status || 'unmonitored');
      element.title = `${plant?.name || id} · ${plant?.status || 'unmonitored'}`;
    }
  }, [plants]);

  useEffect(() => {
    markersRef.current.forEach(({ element, id }) => element.classList.toggle("selected", id === selectedId));
    const selected = plants.find((plant) => plant.id === selectedId);
    if (selected?.coordinates?.length === 2 && selected.coordinates.every(Number.isFinite) && mapRef.current?.loaded()) mapRef.current.easeTo({ center: selected.coordinates, duration: 650, essential: true });
  }, [geometryKey, selectedId]);

  return <div ref={containerRef} className="real-plant-map" aria-label="Interactive plant map" />;
}
